import React, { useState, useEffect } from 'react';
import { 
  FaPlus, 
  FaSearch, 
  FaBarcode, 
  FaEdit, 
  FaTrash, 
  FaExclamationTriangle,
  FaDownload,
  FaPrint,
  FaFilter,
  FaEye
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import BarcodeScannerModal from '../../components/BarcodeScannerModal';

const InventoryPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [showLowStock, setShowLowStock] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [lowStockItems, setLowStockItems] = useState([]);  const [expiredItems, setExpiredItems] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [scannerModalOpen, setScannerModalOpen] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchInventoryItems();
    fetchCategories();
    fetchLowStockItems();
    fetchExpiredItems();
  }, []);

  useEffect(() => {
    filterItems();
  }, [inventoryItems, searchTerm, selectedCategory, showLowStock, showExpired]);

  const fetchInventoryItems = async () => {
    try {
      const response = await fetch('/api/inventory', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInventoryItems(data.items || []);
      } else {
        toast.error('Failed to fetch inventory items');
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Error fetching inventory items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/inventory/categories', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchLowStockItems = async () => {
    try {
      const response = await fetch('/api/inventory/low-stock', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLowStockItems(data);
      }
    } catch (error) {
      console.error('Error fetching low stock items:', error);
    }
  };

  const fetchExpiredItems = async () => {
    try {
      const response = await fetch('/api/inventory/expired', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setExpiredItems(data);
      }
    } catch (error) {
      console.error('Error fetching expired items:', error);
    }
  };
  const searchByBarcode = async (barcode = barcodeSearch) => {
    const searchCode = barcode || barcodeSearch;
    if (!searchCode.trim()) {
      toast.error('Please enter a barcode to search');
      return;
    }

    try {
      const response = await fetch(`/api/inventory/barcode/${searchCode}`);
      if (response.ok) {
        const item = await response.json();
        setFilteredItems([item]);
        setSearchTerm(searchCode);
        setBarcodeSearch(searchCode);
        toast.success('Item found!');
      } else {
        toast.error('Item not found');
      }
    } catch (error) {
      console.error('Error searching by barcode:', error);
      toast.error('Error searching for item');
    }
  };

  const filterItems = () => {
    let filtered = [...inventoryItems];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.barcode.includes(searchTerm) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Apply low stock filter
    if (showLowStock) {
      filtered = filtered.filter(item => 
        item.quantity <= item.minimumQuantity
      );
    }

    // Apply expired filter
    if (showExpired) {
      const today = new Date();
      filtered = filtered.filter(item => 
        item.expirationDate && new Date(item.expirationDate) < today
      );
    }

    setFilteredItems(filtered);
    setCurrentPage(1);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      const response = await fetch(`/api/inventory/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Item deleted successfully');
        fetchInventoryItems();
        setDeleteModalOpen(false);
        setItemToDelete(null);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Error deleting item');
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    try {
      const response = await fetch(`/api/inventory/${itemId}/quantity`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ quantity: newQuantity })
      });

      if (response.ok) {
        toast.success('Quantity updated successfully');
        fetchInventoryItems();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update quantity');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast.error('Error updating quantity');
    }
  };

  const exportInventory = async () => {
    try {
      const response = await fetch('/api/inventory/report', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // Convert to CSV
        const csv = convertToCSV(data);
        downloadCSV(csv, 'inventory-report.csv');
        toast.success('Inventory exported successfully');
      } else {
        toast.error('Failed to export inventory');
      }
    } catch (error) {
      console.error('Error exporting inventory:', error);
      toast.error('Error exporting inventory');
    }
  };

  const convertToCSV = (data) => {
    const headers = ['SKU', 'Name', 'Category', 'Current Quantity', 'Minimum Quantity', 'Unit Cost', 'Unit Price', 'Location', 'Supplier', 'Expiration Date'];
    const rows = data.map(item => [
      item.sku,
      item.name,
      item.category,
      item.quantity,
      item.minimumQuantity,
      item.costPrice,
      item.unitPrice,
      item.location,
      item.supplier,
      item.expirationDate || ''
    ]);
    
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);

  const getStockStatus = (item) => {
    if (item.quantity === 0) return { text: 'Out of Stock', color: 'text-red-600 bg-red-100' };
    if (item.quantity <= item.minimumQuantity) return { text: 'Low Stock', color: 'text-orange-600 bg-orange-100' };
    return { text: 'In Stock', color: 'text-green-600 bg-green-100' };
  };

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Inventory Management</h1>        <div className="flex space-x-3">
          <button
            onClick={() => setScannerModalOpen(true)}
            className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 flex items-center space-x-2"
          >
            <FaBarcode />
            <span>Scan</span>
          </button>
          <button
            onClick={exportInventory}
            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center space-x-2"
          >
            <FaDownload />
            <span>Export</span>
          </button>
          {(user.role === 'admin' || user.role === 'manager') && (
            <Link
              to="/admin/inventory/new"
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center space-x-2"
            >
              <FaPlus />
              <span>Add Item</span>
            </Link>
          )}
        </div>
      </div>

      {/* Alert Cards */}
      {(lowStockItems.length > 0 || expiredItems.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {lowStockItems.length > 0 && (
            <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded">
              <div className="flex items-center">
                <FaExclamationTriangle className="text-orange-400 mr-3" />
                <div>
                  <h3 className="text-lg font-medium text-orange-800">Low Stock Alert</h3>
                  <p className="text-orange-700">{lowStockItems.length} items need restocking</p>
                </div>
              </div>
            </div>
          )}
          {expiredItems.length > 0 && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <div className="flex items-center">
                <FaExclamationTriangle className="text-red-400 mr-3" />
                <div>
                  <h3 className="text-lg font-medium text-red-800">Expired Items</h3>
                  <p className="text-red-700">{expiredItems.length} items have expired</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search and Filter Controls */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search items..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex">
            <input
              type="text"
              placeholder="Search by barcode..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={barcodeSearch}
              onChange={(e) => setBarcodeSearch(e.target.value)}
            />            <button
              onClick={() => searchByBarcode()}
              className="bg-blue-500 text-white px-4 py-2 rounded-r-lg hover:bg-blue-600"
            >
              <FaBarcode />
            </button>
          </div>

          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showLowStock}
                onChange={(e) => setShowLowStock(e.target.checked)}
                className="mr-2"
              />
              Low Stock
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showExpired}
                onChange={(e) => setShowExpired(e.target.checked)}
                className="mr-2"
              />
              Expired
            </label>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU/Barcode</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((item) => {
                const stockStatus = getStockStatus(item);
                const expired = isExpired(item.expirationDate);
                
                return (
                  <tr key={item.id} className={expired ? 'bg-red-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        {item.description && (
                          <div className="text-sm text-gray-500">{item.description.substring(0, 50)}...</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{item.sku}</div>
                      <div className="text-sm text-gray-500">{item.barcode}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {item.quantity} / {item.minimumQuantity} min
                      </div>
                      <div className="text-xs text-gray-500">{item.unit}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">${item.unitPrice}</div>
                      <div className="text-xs text-gray-500">Cost: ${item.costPrice}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}>
                        {stockStatus.text}
                      </span>
                      {expired && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-red-600 bg-red-100 ml-1">
                          Expired
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/admin/inventory/${item.id}`}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <FaEye />
                        </Link>
                        {(user.role === 'admin' || user.role === 'manager') && (
                          <>
                            <Link
                              to={`/admin/inventory/${item.id}/edit`}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit"
                            >
                              <FaEdit />
                            </Link>
                            <button
                              onClick={() => {
                                const newQuantity = prompt('Enter new quantity:', item.quantity);
                                if (newQuantity !== null && !isNaN(newQuantity)) {
                                  updateQuantity(item.id, parseInt(newQuantity));
                                }
                              }}
                              className="text-green-600 hover:text-green-900"
                              title="Update Quantity"
                            >
                              <FaPlus />
                            </button>
                          </>
                        )}
                        {user.role === 'admin' && (
                          <button
                            onClick={() => handleDeleteClick(item)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, filteredItems.length)}</span> of{' '}
                  <span className="font-medium">{filteredItems.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === i + 1
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <FaTrash className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Inventory Item</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300"
                >
                  Delete
                </button>
                <button
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setItemToDelete(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md w-24 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>          </div>
        </div>
      )}

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal
        isOpen={scannerModalOpen}
        onClose={() => setScannerModalOpen(false)}
        onScanResult={(barcode) => searchByBarcode(barcode)}
      />
    </div>
  );
};

export default InventoryPage;
